import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { AxiosCartError, Cart } from '@telialt/models/src/EshopModel';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';
import { PARAM_GUID_QUERY, PARAM_ID_QUERY } from '@telialt/utils/src/eshopConstants';
import { selectFailureReason, selectPopPaymentStatus } from '@telialt/utils/src/eshopSelectors';
import {
    getCartIdSync,
    getCartSession,
    getOrderPlacedId,
    getPreviousProduct,
    isCartLoadedFromUrl,
    setPreviousProduct,
} from '@telialt/utils/src/eshopUtils';
import QueryString from '@telialt/utils/src/QueryString';
import { useSegment } from '@telialt/utils/src/segment';
import { getIsUserAuthorized } from '@telialt/utils/src/SSO/utils';

import { ESHOP_QUERY_KEY } from '../../model';
import { apiCreateCart, apiGetCart, apiMigrateCart } from '../../store/api';

export const useStrategyCreateCartQuery = (baseProductCode: string, config?: { onError: () => void }) => {
    const segment = useSegment();

    const query = useQuery<Cart, AxiosCartError>({
        queryKey: [ESHOP_QUERY_KEY.CART],
        queryFn: async ({ signal }) => {
            const { search } = window.location;
            const cartSession = getCartSession();
            const previousProductCode = getPreviousProduct();
            setPreviousProduct(baseProductCode);

            const isDifferentProduct =
                baseProductCode && previousProductCode && baseProductCode !== previousProductCode;
            const isPreviouslyCloned = cartSession?.isPreviouslyCloned;

            if (isDifferentProduct || (!isDifferentProduct && isPreviouslyCloned)) {
                return (await apiCreateCart(segment)).data;
            }

            if (isCartLoadedFromUrl(search)) {
                const queryString = new QueryString(search);
                const guid = queryString.getParam(PARAM_GUID_QUERY);
                const code = queryString.getParam(PARAM_ID_QUERY);

                return (await apiGetCart(segment, { cartId: guid || code })).data;
            }

            if (cartSession?.isCartAnonymous && getIsUserAuthorized()) {
                return (await apiMigrateCart(segment, { oldCartId: cartSession.guid })).data;
            }

            if (!cartSession?.isCartAnonymous && !getIsUserAuthorized()) {
                return (await apiCreateCart(segment)).data;
            }

            if (!cartSession) {
                return (await apiCreateCart(segment)).data;
            }

            const cartIdFromSession = getCartIdSync(cartSession);

            if (cartIdFromSession === getOrderPlacedId()) {
                return (await apiCreateCart(segment)).data;
            }

            const cartFromSession = (await apiGetCart(segment, { cartId: cartIdFromSession }, null, signal)).data;
            if (selectFailureReason(cartFromSession) || selectPopPaymentStatus(cartFromSession)) {
                return (await apiCreateCart(segment)).data;
            }

            return cartFromSession;
        },

        staleTime: 3000,
    });
    useEffect(() => {
        if (query.error) {
            config?.onError?.();
        }
        // should trigger "onError" callback only on error change
    }, [query.error]);

    useLogError(query.error, DDComponent.Eshop);

    return query;
};
